import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Settings as SettingsIcon,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { MobilePageTitle } from "../components/MobilePageTitle";
import { SelectorMarcaMobile } from "../components/SelectorMarcaMobile";
import { useBrandProfile } from "../hooks/useBrandProfile";
import { MiMarcaProductsTab } from "../components/MiMarcaProductsTab";
import { TiendaNubeStatus } from "../components/TiendaNubeStatus";
import { MiMarcaModelsTab } from "../components/MiMarcaModelsTab";
import { CompanyData } from "./config_sections/CompanyData";
import {
  BLOQUE_ESQUELETO,
  EsqueletoSeccion,
  claseContenido,
} from "../components/EsqueletoSeccion";

type TabKey = "productos" | "diseno" | "modelos";
/**
 * Orden y nombres de los tabs — ADR-2026-08-04-marca-abre-en-productos
 * (supersede ADR-2026-08-03-mi-marca-tabs-diseno-primero).
 *
 * "Productos" va primero y es el que abre por defecto: es a lo que se entra a esta
 * pantalla —cargar y revisar el material con el que se genera—, mientras que la
 * información de la marca se completa una vez y casi no se vuelve a tocar. Antes
 * abría en "Información" por ser lo que define la marca conceptualmente, pero eso no
 * es lo que el usuario viene a hacer acá.
 *
 * Los "Mi/Mis" se cayeron: el título de la pantalla ya dice "Marca", así que cada
 * ítem lo repetía. La etiqueta de `diseno` es "Información" —adentro se cargan
 * nombre, rubro, descripción y paleta, los DATOS de la marca—; la `key` sigue siendo
 * `diseno` a propósito: es interna y renombrarla no le cambia nada al usuario.
 *
 * El primero de la lista y el que arranca marcado son el mismo, que es lo que uno
 * espera al entrar.
 */
const TABS: { key: TabKey; label: string }[] = [
  { key: "productos", label: "Productos" },
  { key: "modelos", label: "Modelos" },
  { key: "diseno", label: "Información" },
];

/**
 * Deep-link `?tab=` → qué ítem abre — ADR-2026-08-05-editar-marca-abre-informacion.
 *
 * La tabla existe para no meter las `key` internas en la URL: `diseno` es un nombre
 * viejo que en pantalla dice "Información" (ver el comentario de `TABS`), y una URL que
 * se puede copiar, compartir o guardar es lo último que conviene atar a él.
 */
const TAB_POR_SLUG: Record<string, TabKey> = {
  productos: "productos",
  modelos: "modelos",
  informacion: "diseno",
};

/**
 * Pantalla "Mi marca": un selector de tabs (Mis productos · Mi diseño · Mis
 * Modelos) con el contenido del tab elegido al costado.
 *
 * Antes abría con una card de marca (logo + usuario + marca + plan, que además
 * era el acceso a "Cambiar de marca") y un bloque de créditos restantes. Los dos
 * se quitaron —ADR-2026-07-31-rubro-selector-sale-marca-real y ADR-2026-07-31-mi-marca-card-marca-pantalla—: eran identidad y saldo, que el shell ya muestra
 * en la sidebar y en la barra de créditos desde cualquier pantalla.
 */
export const MiMarca = () => {
  const navigate = useNavigate();
  const {
    profile,
    currentBrandId,
    activeBrand,
    loading: cargandoMarca,
    // Para el selector de marca de arriba (sólo abajo de `lg`, donde no está la
    // sidebar del shell) — ADR-2026-08-04-cambiar-de-marca-desde-la-pantalla-de-marca.
    brands: marcas,
    switchBrand,
    switching: cambiandoMarca,
  } = useBrandProfile();

  /**
   * La pantalla entra entera o va en esqueleto —
   * ADR-2026-08-03-todas-las-secciones-cargan-igual.
   *
   * Espera sólo a `useBrandProfile` (perfil y marca activa). Antes esperaba TAMBIÉN a
   * que `CompanyData` avisara con `onListo`, porque el tab que abría era
   * "Información" y esas tarjetas eran el contenido de la primera pintura. Con
   * "Productos" por defecto (ADR-2026-08-04-marca-abre-en-productos) ese hijo ya no se
   * monta al entrar, así que su aviso no llegaría nunca y `listo` se quedaba en falso
   * para siempre: `claseContenido(false)` es `hidden`, o sea la pantalla entera
   * invisible detrás del esqueleto.
   *
   * No hace falta reemplazarlo por un aviso del tab de productos: ese trae su propio
   * esqueleto de grilla mientras carga, igual que `CompanyData` trae su spinner
   * cuando se lo abre después.
   */
  const listo = !cargandoMarca;

  const [searchParams, setSearchParams] = useSearchParams();

  // El tab inicial sale de `?tab=` si vino uno válido (ver `TAB_POR_SLUG`), y si no del
  // default de la pantalla. Va en el inicializador y no sólo en el efecto de abajo para
  // que la primera pintura ya sea la correcta: con el efecto solo se vería un cuadro de
  // Productos antes del salto.
  const [tab, setTab] = useState<TabKey>(
    () => TAB_POR_SLUG[searchParams.get("tab") ?? ""] ?? "productos",
  );

  /**
   * Deep-link `?tab=` → abrir ese ítem, incluso ESTANDO YA en la pantalla.
   *
   * El inicializador de arriba sólo corre al montar, y "Editar marca actual" de la
   * sidebar se puede tocar desde `/mi-marca` mismo: ahí no hay montaje, cambia la URL y
   * nada más. Sin este efecto el ítem no hacía nada justo desde la pantalla donde el
   * usuario ya está parado.
   *
   * El param se limpia después de aplicarlo, por lo mismo que `conectar`: si quedara,
   * cambiar de tab a mano dejaría la URL diciendo otra cosa. `replace` para no ensuciar
   * el historial.
   */
  useEffect(() => {
    const destino = TAB_POR_SLUG[searchParams.get("tab") ?? ""];
    if (!destino) return;
    setTab(destino);
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // ── Deep-link `?conectar=tiendanube` (banner "Conectá tu Tienda Nube" de la
  //    home) → abre el modal de conexión solo, sin que el user tenga que buscar
  //    el botón.
  //
  //    El estado vive ACÁ y no en `TiendaNubeStatus` porque hay DOS instancias
  //    montadas a la vez (ícono mobile + card desktop, ocultas por CSS). Se le
  //    pasa a UNA sola (la card): su modal usa `createPortal` a document.body,
  //    así que se ve bien igual aunque en mobile su contenedor esté oculto.
  //    Si se le pasara a las dos, se abrirían dos modales superpuestos.
  const [tnConnectOpen, setTnConnectOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get("conectar") !== "tiendanube") return;
    setTnConnectOpen(true);
    // Limpiar el param para que el modal no se re-abra al volver atrás o al
    // refrescar. `replace` para no ensuciar el historial.
    const next = new URLSearchParams(searchParams);
    next.delete("conectar");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  // Error/success para el componente CompanyData (Mi diseño).
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  return (
    <>
      {!listo && (
        // Las clases de la fila del título son las MISMAS que las de la fila real de
        // abajo: es lo que hace que el contenido entre justo donde estaba el gris.
        <EsqueletoSeccion
          className="px-4 sm:px-8 pt-2 max-w-content mx-auto"
          anchoTitulo="w-[200px]"
          claseFilaTitulo="hidden sm:flex items-center justify-between mb-5 h-11 lg:mb-0 lg:h-auto lg:pt-4 lg:pb-8"
        >
          <div className="lg:grid lg:grid-cols-[440px_minmax(0,1fr)] lg:gap-8 lg:items-start">
            {/* Izquierda: los tres tabs y el bloque de integración. */}
            <div className="space-y-2.5">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className={`h-[57px] rounded-2xl bg-black/[0.06] animate-pulse`} />
              ))}
              <div className="pt-6">
                <div className={`h-4 w-24 ${BLOQUE_ESQUELETO} mb-2`} />
                <div className={`h-[57px] rounded-2xl bg-black/[0.06] animate-pulse`} />
              </div>
            </div>
            {/* Derecha: el tab de Productos, que es el que abre
                (ADR-2026-08-04-marca-abre-en-productos) — la fila de buscador +
                botón y la grilla de miniaturas. */}
            <div className="mt-5 space-y-4 lg:mt-0">
              <div className="flex items-center gap-3">
                <div className={`h-10 flex-1 rounded-xl bg-black/[0.06] animate-pulse`} />
                <div className={`h-10 w-32 rounded-xl bg-black/[0.06] animate-pulse`} />
              </div>
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className={`aspect-square rounded-xl bg-black/[0.06] animate-pulse`} />
                ))}
              </div>
            </div>
          </div>
        </EsqueletoSeccion>
      )}
    {/* `pt-0` en mobile: el título arrancaba 12px más abajo de lo necesario —el
        header flotante ya reserva su alto arriba— y la pantalla se veía hundida.
        Desktop conserva su `pt-2`. */}
    <div className={`relative px-4 sm:px-8 pt-0 sm:pt-2 pb-[calc(env(safe-area-inset-bottom,0px)+6rem)] md:pb-8 max-w-content mx-auto min-h-screen ${claseContenido(listo)}`}>
      {/* Título mobile (desktop usa el header de abajo) con su acción a la derecha:
          Configuración, que en desktop va por el sidebar. Con etiqueta y con caja
          —ADR-2026-08-04-tienda-nube-y-configuracion-con-label—: era un engranaje
          pelado, que no decía a dónde llevaba ni se leía como algo tocable.
          Tiendanube salió de acá: ahora es una fila debajo del selector de marca.

          Va como `accion` del título y no `absolute` con un `top` calculado: en la
          misma fila, alinearlo es `items-center`
          (ADR-2026-08-04-la-accion-del-titulo-vive-en-su-fila). */}
      <MobilePageTitle
        accion={
          <button
            type="button"
            onClick={() => navigate("/configuracion")}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-black/[0.07] bg-white px-3 text-[13.5px] font-medium text-brand-black/80 transition-opacity active:opacity-70"
          >
            <SettingsIcon className="h-4 w-4 text-brand-black/60" />
            Configuración
          </button>
        }
      >
        Marca
      </MobilePageTitle>

      {/* Cambio de marca — ADR-2026-08-04-cambiar-de-marca-desde-la-pantalla-de-marca.
          `lg:hidden` porque de 1024px para arriba lo hace la tarjeta de la sidebar del
          shell nuevo; abajo de ese ancho esa sidebar no existe y esta pantalla se había
          quedado sin ningún acceso propio (ver el comentario de la card vieja, más
          abajo). Va arriba de los tabs a propósito: dice de qué marca son los productos
          y los modelos que siguen. */}
      <div className="lg:hidden mb-4">
        <SelectorMarcaMobile
          marcas={marcas}
          marcaActivaId={currentBrandId}
          cambiarMarca={switchBrand}
          cambiando={cambiandoMarca}
        />
        {/* Tiendanube, debajo del selector — ADR-2026-08-04-tienda-nube-y-configuracion-con-label.
            `md:hidden` y no `lg:hidden` como el selector: de 768px para arriba la
            integración ya la muestra la card de la columna izquierda, y las dos a la
            vez serían el mismo control dos veces en la misma pantalla.
            Debajo y no arriba: la conexión es DE esta marca, así que primero hay que
            saber cuál es. */}
        <div className="md:hidden mt-2.5">
          <TiendaNubeStatus
            variant="boton"
            companyId={profile?.id_company ?? null}
            brandId={currentBrandId}
          />
        </div>
      </div>
      {/* Desktop header. Acá era sticky arriba, con fondo `surface-content` para
          taparte el contenido que pasaba por debajo. Las dos cosas se fueron en
          ADR-2026-08-03-titulos-secciones-igual-que-inicio: el título tiene que
          leerse igual que el saludo de `/inicio`, que no lleva franja ni se
          queda pegado. El shell además pisa `position` y `background` desde
          `.shell-seccion` — esto es sacarlo también del markup, no dejarlo
          peleando contra el CSS. */}
      <div className="hidden sm:flex items-center justify-between mb-5 h-11 lg:mb-0 lg:h-auto lg:pt-4 lg:pb-8">
        <h1 className="text-[17px] font-semibold text-brand-black/90 mt-4 lg:mt-0">
          Marca
        </h1>
      </div>

      {/* Layout: 1 columna hasta lg; en lg+ dos columnas (izq: marca +
          créditos + tabs verticales / der: contenido del tab). Usamos lg y no
          md porque a md el sidebar + columna izquierda dejan el área derecha
          demasiado angosta. */}
      <div className="lg:grid lg:grid-cols-[440px_minmax(0,1fr)] lg:gap-8 lg:items-start">
        {/* ── Columna izquierda (fija al scrollear en desktop) ──
            Los 60px de offset venían de dejarle lugar al título sticky, que ya
            no lo es (ADR-2026-08-03-titulos-secciones-igual-que-inicio). Se
            conservan igual: ahora son sólo aire arriba de la columna cuando se
            pega, y bajarlos a 0 la dejaría lamiendo el borde del scroller. */}
        <div className="lg:sticky lg:top-[60px] lg:self-start">
          {/* Acá iban, uno debajo del otro, la card de marca (logo + usuario +
              marca + plan, que además abría el switcher) y el bloque "Créditos
              restantes" con su barra. Los dos se quitaron — ADR-2026-07-31-rubro-selector-sale-marca-real y ADR-2026-07-31-mi-marca-card-marca-pantalla.

              La columna arranca directamente en los tabs: esta pantalla es para
              editar la marca, y lo que se sacó era identidad y saldo, que se ven
              en el shell (sidebar + barra de créditos) desde cualquier pantalla.

              ⚠️ Con la card se fue el ÚNICO acceso a "Cambiar de marca" que tenía
              esta pantalla. El de `Sidebar.tsx` sigue existiendo, pero el shell
              nuevo no tiene ninguno todavía: su selector de marca navega acá en
              vez de desplegar la lista. Ver ADR-2026-07-31-mi-marca-card-marca-pantalla. */}

          {/* Tabs horizontales (mobile / hasta lg) */}
          <div className="lg:hidden flex border-b border-black/[0.08]">
            {TABS.map((t) => {
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`flex-1 pb-2.5 text-[13.5px] font-medium border-b-2 -mb-px transition-colors ${
                    isActive
                      ? "border-brand-primary text-brand-primary"
                      : "border-transparent text-brand-black/50 hover:text-brand-black/80"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Tabs como containers verticales (lg+) */}
          <div className="hidden lg:flex lg:flex-col gap-2">
            {TABS.map((t) => {
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  // Seleccionado: el MISMO tratamiento que un ítem de la sidebar
                  // (ADR-2026-08-03-mi-marca-tabs-diseno-primero) — relleno
                  // `#EBEFFE` y texto en el azul de marca. Antes era
                  // `bg-brand-primary/[0.06]` con `border-brand-primary/40`, y
                  // ninguna de las dos generaba regla: `brand.primary` está
                  // declarado como `var(--brand-primary)` sin `<alpha-value>`, así
                  // que Tailwind no le puede aplicar la opacidad y no emite nada. El
                  // ítem activo quedaba SIN fondo —el gris de la página se veía a
                  // través— mientras los otros tres sí eran blancos: el
                  // seleccionado se leía como el apagado.
                  //
                  // El borde se vuelve transparente en vez de desaparecer: mantiene
                  // la caja del mismo tamaño que los no seleccionados, y el relleno
                  // sólido es el que marca el estado, igual que en la sidebar.
                  //
                  // Los no seleccionados no declaran color de texto: heredan el
                  // `#16181D` del shell, que es lo que ya venían mostrando
                  // (`text-brand-black/80` tampoco generaba regla).
                  className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border text-[14px] font-medium transition-colors ${
                    isActive
                      ? "border-transparent bg-[#EBEFFE] text-brand-primary"
                      : "border-black/[0.07] bg-white hover:bg-black/[0.02]"
                  }`}
                >
                  {t.label}
                  <ChevronRight
                    className={`w-4 h-4 transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`}
                  />
                </button>
              );
            })}
          </div>

          {/* Integración Tiendanube — desde md+ (tablet/desktop) va acá, separada
              de los tabs con una divisoria + rótulo. En mobile va como ícono en
              el header (arriba a la derecha). */}
          <div className="hidden md:block mt-6 pt-5 border-t border-black/[0.08]">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-black/35 mb-2 px-1">
              Integración
            </p>
            {/* Única instancia con el modal controlado — ver el comentario del
                deep-link arriba. La instancia mobile (ícono) sigue con su
                estado interno. */}
            <TiendaNubeStatus
              companyId={profile?.id_company ?? null}
              brandId={currentBrandId}
              connectOpen={tnConnectOpen}
              onConnectOpenChange={setTnConnectOpen}
            />
          </div>
        </div>

        {/* ── Columna derecha: contenido del tab seleccionado ── */}
        <div className="mt-5 lg:mt-0">
          {/* Banners de error/success (para Mi diseño) */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[14px] text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2.5 text-[14px] text-green-700">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <p>{success}</p>
            </div>
          )}

          {tab === "productos" && (
            <MiMarcaProductsTab
              companyId={profile?.id_company ?? null}
              brandId={currentBrandId}
              // Hasta que `useBrandProfile` no resuelve, `currentBrandId` es `null` y
              // eso para `fetchUserProducts` significa "todas las marcas", no "todavía
              // no sé" (ADR-2026-08-04-los-productos-esperan-a-saber-de-que-marca-son).
              marcaResuelta={!cargandoMarca}
              // El rubro de la marca ACTIVA, con el de la empresa de respaldo — el
              // mismo orden que usan la sidebar y la home. Lo necesita el sheet de
              // modos para saber si ofrece Plantillas
              // (ADR-2026-08-04-desde-un-producto-se-elige-el-modo). Sale de acá y no
              // de un `useBrandProfile` propio del tab: ese hook ya está montado en
              // esta pantalla y duplicarlo sería una segunda ronda de queries por las
              // mismas filas.
              rubro={activeBrand?.rubro ?? profile?.rubro ?? null}
            />
          )}
          {tab === "diseno" &&
            (profile?.id_company ? (
              <CompanyData
                profile={profile}
                setError={setError}
                setSuccess={setSuccess}
                embedded
              />
            ) : (
              <div className="flex justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-brand-black/30" />
              </div>
            ))}
          {tab === "modelos" && (
            <MiMarcaModelsTab
              companyId={profile?.id_company ?? null}
              // Mismo orden que el tab de productos (marca activa → empresa): lo
              // necesita el sheet de modos para saber si ofrece Plantillas.
              rubro={activeBrand?.rubro ?? profile?.rubro ?? null}
            />
          )}
        </div>
      </div>

      {/* El modal de "Cambiar de marca" se fue con la card que lo abría (ADR-2026-07-31-mi-marca-card-marca-pantalla):
          sin ese botón quedaba inalcanzable. Si hace falta reponerlo, la
          implementación equivalente y viva está en `Sidebar.tsx`. */}
    </div>
    </>
  );
};
